import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { EventinfoText } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputText,
    ValidatedText,
    InputDateTime,
    DateComp,
    InputSelect,
    DataSourceLookup,
    InputBool,
    DisplayBool,
    HtmlEditor,
} from '@/components';
import { type DateType } from '@/utilities/dateutils';
import { useCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';
import { useValidator } from '@/composeables';

export class EventinfoTextGuiDef extends GuiDef<EventinfoText> {
    constructor() {
        super('EventinfoText');

        const Common = storeToRefs(useCommonStore());
        const LocalesSource = computed(() => {
            return Common.LocalesAsSelectLine.value;
        });

        const TitleValidator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        const Title = new FieldDef<EventinfoText>();
        Title.Name = 'Title';
        Title.FieldName = 'Title';
        Title.DefinedFor = 'Both';
        Title.TranslationKey = 'eventinfotext.title';
        Title.Editable = true;
        Title.Filterable = true;
        Title.Sortable = true;
        Title.CssClasses = '';
        Title.value = (eventinfotext) => eventinfotext.Title;
        Title.setValue = (eventinfotext, value) => (eventinfotext.Title = value);
        Title.DetailEditor = markRaw(InputText);
        Title.DetailEditorProps = {};
        Title.CellEditor = markRaw(InputText);
        Title.CellEditorProps = {};
        Title.CellRenderer = markRaw(ValidatedText);
        Title.CellRendererProps = {};
        Title.Validator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        Title.SortDirection = undefined;
        Title.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(TitleValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(TitleValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        Title.MinWidth = undefined;
        Title.MaxWidth = undefined;
        Title.Tooltip = null;
        this.Fields.push(Title);

        const Created = new FieldDef<EventinfoText>();
        Created.Name = 'Created';
        Created.FieldName = 'Created';
        Created.DefinedFor = 'Both';
        Created.TranslationKey = 'eventinfotext.created';
        Created.Editable = false;
        Created.Filterable = true;
        Created.Sortable = true;
        Created.CssClasses = '';
        Created.value = (eventinfotext) => eventinfotext.Created;
        Created.setValue = () => {};
        Created.DetailEditor = markRaw(InputDateTime);
        Created.DetailEditorProps = { format: 'DATETIME' as DateType, readonly: true };
        Created.CellEditor = markRaw(InputDateTime);
        Created.CellEditorProps = { format: 'DATETIME' as DateType, readonly: true };
        Created.CellRenderer = markRaw(DateComp);
        Created.CellRendererProps = { format: 'DATETIME' as DateType, readonly: true };
        Created.Validator = null;
        Created.SortDirection = undefined;
        Created.cellClassRules = undefined;
        Created.MinWidth = 180;
        Created.MaxWidth = undefined;
        Created.Tooltip = null;
        this.Fields.push(Created);

        const LastChanged = new FieldDef<EventinfoText>();
        LastChanged.Name = 'LastChanged';
        LastChanged.FieldName = 'LastChanged';
        LastChanged.DefinedFor = 'Both';
        LastChanged.TranslationKey = 'eventinfotext.lastchanged';
        LastChanged.Editable = false;
        LastChanged.Filterable = true;
        LastChanged.Sortable = true;
        LastChanged.CssClasses = '';
        LastChanged.value = (eventinfotext) => eventinfotext.LastChanged;
        LastChanged.setValue = () => {};
        LastChanged.DetailEditor = markRaw(InputDateTime);
        LastChanged.DetailEditorProps = { format: 'DATETIME' as DateType, readonly: true };
        LastChanged.CellEditor = markRaw(InputDateTime);
        LastChanged.CellEditorProps = { format: 'DATETIME' as DateType, readonly: true };
        LastChanged.CellRenderer = markRaw(DateComp);
        LastChanged.CellRendererProps = { format: 'DATETIME' as DateType, readonly: true };
        LastChanged.Validator = null;
        LastChanged.SortDirection = undefined;
        LastChanged.cellClassRules = undefined;
        LastChanged.MinWidth = 180;
        LastChanged.MaxWidth = undefined;
        LastChanged.Tooltip = null;
        this.Fields.push(LastChanged);

        const LanguageValidator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        const Language = new FieldDef<EventinfoText>();
        Language.Name = 'Language';
        Language.FieldName = 'Language';
        Language.DefinedFor = 'Both';
        Language.TranslationKey = 'eventinfotext.language';
        Language.Editable = true;
        Language.Filterable = false;
        Language.Sortable = true;
        Language.CssClasses = '';
        Language.value = (eventinfotext) => eventinfotext.Language;
        Language.setValue = (eventinfotext, value) => (eventinfotext.Language = value);
        Language.DetailEditor = markRaw(InputSelect);
        Language.DetailEditorProps = { datasource: { mapsource: LocalesSource }, defaultfirst: true };
        Language.CellEditor = markRaw(InputSelect);
        Language.CellEditorProps = { datasource: { mapsource: LocalesSource }, defaultfirst: true };
        // @ts-expect-error @ts-ignore
        Language.CellRenderer = markRaw(DataSourceLookup);
        Language.CellRendererProps = { datasource: { mapsource: LocalesSource } };
        Language.ExportValueFormatter = (obj: EventinfoText) => {
            return LocalesSource.value.get(obj.Language)?.text;
        };
        Language.Validator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        Language.SortDirection = undefined;
        Language.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(LanguageValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(LanguageValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        Language.MinWidth = undefined;
        Language.MaxWidth = undefined;
        Language.Tooltip = null;
        this.Fields.push(Language);

        const Active = new FieldDef<EventinfoText>();
        Active.Name = 'Active';
        Active.FieldName = 'Active';
        Active.DefinedFor = 'Both';
        Active.TranslationKey = 'eventinfotext.active';
        Active.Editable = true;
        Active.Filterable = false;
        Active.Sortable = true;
        Active.CssClasses = '';
        Active.value = (eventinfotext) => eventinfotext.Active;
        Active.setValue = (eventinfotext, value) => (eventinfotext.Active = value);
        Active.DetailEditor = markRaw(InputBool);
        Active.DetailEditorProps = {};
        Active.CellEditor = markRaw(InputBool);
        Active.CellEditorProps = {};
        Active.CellRenderer = markRaw(DisplayBool);
        Active.CellRendererProps = {};
        Active.Validator = null;
        Active.SortDirection = undefined;
        Active.cellClassRules = undefined;
        Active.MinWidth = undefined;
        Active.MaxWidth = undefined;
        Active.Tooltip = null;
        this.Fields.push(Active);

        const Ingress = new FieldDef<EventinfoText>();
        Ingress.Name = 'Ingress';
        Ingress.FieldName = 'Ingress';
        Ingress.DefinedFor = 'Detail';
        Ingress.TranslationKey = 'eventinfotext.ingress';
        Ingress.Editable = true;
        Ingress.Filterable = true;
        Ingress.Sortable = true;
        Ingress.CssClasses = '';
        Ingress.value = (eventinfotext) => eventinfotext.Ingress;
        Ingress.setValue = (eventinfotext, value) => (eventinfotext.Ingress = value);
        Ingress.DetailEditor = markRaw(HtmlEditor);
        Ingress.DetailEditorProps = {};
        Ingress.CellEditor = markRaw(InputText);
        Ingress.CellEditorProps = {};
        Ingress.CellRenderer = markRaw(ValidatedText);
        Ingress.CellRendererProps = {};
        Ingress.Validator = null;
        Ingress.SortDirection = undefined;
        Ingress.cellClassRules = undefined;
        Ingress.MinWidth = undefined;
        Ingress.MaxWidth = undefined;
        Ingress.Tooltip = null;
        this.Fields.push(Ingress);

        const Text = new FieldDef<EventinfoText>();
        Text.Name = 'Text';
        Text.FieldName = 'Text';
        Text.DefinedFor = 'Detail';
        Text.TranslationKey = 'eventinfotext.text';
        Text.Editable = true;
        Text.Filterable = true;
        Text.Sortable = true;
        Text.CssClasses = '';
        Text.value = (eventinfotext) => eventinfotext.Text;
        Text.setValue = (eventinfotext, value) => (eventinfotext.Text = value);
        Text.DetailEditor = markRaw(HtmlEditor);
        Text.DetailEditorProps = {};
        Text.CellEditor = markRaw(InputText);
        Text.CellEditorProps = {};
        Text.CellRenderer = markRaw(ValidatedText);
        Text.CellRendererProps = {};
        Text.Validator = null;
        Text.SortDirection = undefined;
        Text.cellClassRules = undefined;
        Text.MinWidth = undefined;
        Text.MaxWidth = undefined;
        Text.Tooltip = null;
        this.Fields.push(Text);
    }

    public async LoadDependencies() {
        await useCommonStore().EnsureLoaded(['Locales']);
    }
}
